var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { FocusZone as FluentFocusZone } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { Measures } from "../../../../telemetry/Measures";
import { dashboard } from "../../../../telemetry/TraceLogging";
import { WidgetPickerInteraction } from "../../../utils/widgetDialogUtilities";
import { addWidgetInstance, getWidgetInstancesPromise, getWidgetRegistrationsPromise } from "../../../webUXApi/widgetApi";
import { getUriForResource } from "../../../widgetRuntime/registrar/registrarHelpers";
import { ScrollableStack, ScrollableTelemetryIdentifier } from "../../generic/ScrollableStack/ScrollableStack";
import { getWidgetPickerStyle } from "./WidgetPicker.styles";
import { WidgetPickerItem } from "./WidgetPickerItem/WidgetPickerItem";
import { DispatchOnly } from "../../../../telemetry/DispatchOnly";
export const WidgetPicker = React.memo((_a) => {
    var { onDismiss } = _a, props = __rest(_a, ["onDismiss"]);
    const [widgetInstances, setWidgetInstances] = useState([]);
    const [widgetRegistrations, setWidgetRegistrations] = useState([]);
    function isAddSupportedForWidget(widget) {
        return ((widgetInstances === null || widgetInstances === void 0 ? void 0 : widgetInstances.find((instance) => {
            return instance.widgetRegistration.maxInstances === 1 && instance.widgetDefinitionId === widget.id;
        })) === undefined);
    }
    async function onAddWidget(widgetRegistration, widgetItemPosition) {
        await addWidgetInstance(widgetRegistration);
        // Dismiss Picker after a widget is added, to align with the multi-instance experience
        onDismiss();
        // Log telemetry and share diagnostic data with WebXT
        let partCPayload = {
            addedWidgetDefinitionId: widgetRegistration.id,
            addedWidgetPickerItemIndex: widgetItemPosition,
            appId: widgetRegistration.appId,
            change: WidgetPickerInteraction.AddWidget,
            providerId: widgetRegistration.providerId,
        };
        Measures.logEvent(dashboard, "WidgetPickerInteracted", 33554432 /* productAndServiceUsage */, partCPayload);
        DispatchOnly.logEvent(dashboard, "WidgetPickerInteracted", 1 /* diagnosticData */, 33554432 /* productAndServiceUsage */, partCPayload);
    }
    function pickerComparator(widget1, widget2) {
        if (widget1.displayName < widget2.displayName) {
            return -1;
        }
        if (widget1.displayName > widget2.displayName) {
            return 1;
        }
        return 0;
    }
    useEffect(() => {
        getWidgetInstancesPromise().then((instances) => {
            setWidgetInstances(instances);
            getWidgetRegistrationsPromise().then((registrations) => {
                const initiallyAddableWidgets = [];
                const initiallyNotAddableWidgets = [];
                registrations.forEach(function (widgetItem, i) {
                    const isAddSupported = isAddSupportedForWidget(widgetItem);
                    if (isAddSupported) {
                        initiallyAddableWidgets.push(widgetItem);
                    }
                    else {
                        initiallyNotAddableWidgets.push(widgetItem);
                    }
                });
                initiallyAddableWidgets.sort(pickerComparator);
                initiallyNotAddableWidgets.sort(pickerComparator);
                setWidgetRegistrations(initiallyAddableWidgets.concat(initiallyNotAddableWidgets));
            });
        });
    }, []);
    const renderWidgetPickerItems = (items) => {
        const widgetPickerItems = [];
        const availableWidgetsList = [];
        items.forEach(function (widgetItem, i) {
            const isAddSupported = isAddSupportedForWidget(widgetItem);
            widgetPickerItems.push(React.createElement(WidgetPickerItem, { key: widgetItem.id, title: widgetItem.displayName, icon: getUriForResource(widgetItem.id, widgetItem.icon.src), isAddSupported: isAddSupported, onClick: (event) => {
                    let target = event.currentTarget;
                    onAddWidget(widgetItem, i).then(() => {
                        getWidgetInstancesPromise().then((instances) => {
                            setWidgetInstances(instances);
                            target && (target.disabled = !isAddSupportedForWidget(widgetItem));
                        });
                    });
                } }));
            availableWidgetsList.push({
                appId: widgetItem.appId,
                isAddSupported: isAddSupported,
                providerId: widgetItem.providerId,
                widgetDefinitionId: widgetItem.id,
            });
        });
        if (availableWidgetsList.length !== 0) {
            Measures.logEvent(dashboard, "WidgetPickerAvailableWidgetsList", 16777216 /* productAndServicePerformance */, {
                widgetsList: availableWidgetsList,
            });
        }
        return widgetPickerItems;
    };
    return (React.createElement(FluentFocusZone, { style: { marginRight: "-8px" /* position scrollbar of ScrollableStack */ } },
        React.createElement(ScrollableStack, { scrollableTelemetryIdentifier: ScrollableTelemetryIdentifier.WidgetPickerItems, isScrollTelemetryEnabled: true, horizontal: true, wrap: true, tokens: { childrenGap: 12 }, isScrollbarForcedVisible: true, styles: getWidgetPickerStyle() }, renderWidgetPickerItems(widgetRegistrations))));
});
